import React, { useState, useEffect } from 'react';
import { invoiceImageService } from '../services/InvoiceImageService';
import { useAccount } from '../contexts/AccountContext';
import { orgApi } from '../api';
import { showToast } from '../utils/toast';

// Sample data for preview - defined outside component to prevent recreation
const sampleInvoiceData = {
  billName: 'INV-20241201-001',
  customerName: 'John Doe',
  customerPhone: '+91-9876543210',
  items: [
    { name: 'Product A', price: 100, quantity: 2, unit: 'piece' },
    { name: 'Product B', price: 250, quantity: 1, unit: 'kg' },
    { name: 'Product C', price: 50, quantity: 3, unit: 'piece' }
  ],
  total: 600
};

const InvoiceEditor = ({ open, onClose, order, defaultProductName }) => {
  const { selectedAccountId } = useAccount();
  
  // Business settings state
  const [businessSettings, setBusinessSettings] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    logoDataUrl: '',
    logoFile: null,
    footerText: '' // Custom footer text for invoices
  });
  
  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Load business settings
  useEffect(() => {
    loadBusinessSettings();
  }, []);

  // Cleanup preview image on unmount
  useEffect(() => {
    return () => {
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);
  
  const loadBusinessSettings = async () => {
    try {
      // Load from org API
      const orgData = await orgApi.get();
      if (orgData) {
        setBusinessSettings({
          name: orgData.name || '',
          address: orgData.address || '',
          phone: orgData.phone || '',
          email: orgData.email || '',
          logoDataUrl: orgData.logoDataUrl || '',
          logoFile: null,
          footerText: orgData.footerText || '' // Load footer text if available
        });
      }
    } catch (error) {
      console.error('Failed to load business settings:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Save business settings
  const saveBusinessSettings = async () => {
    try {
      await orgApi.update({
        name: businessSettings.name,
        address: businessSettings.address,
        phone: businessSettings.phone,
        email: businessSettings.email,
        logoDataUrl: businessSettings.logoDataUrl,
        footerText: businessSettings.footerText
      });
      
      showToast({ message: 'Business settings saved successfully!', variant: 'success' });
    } catch (error) {
      console.error('Failed to save business settings:', error);
      showToast({ message: 'Failed to save settings. Please try again.', variant: 'error' });
    }
  };
  
  // Handle logo upload with size compression
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Check file size (max 5MB - we'll compress it)
    if (file.size > 5242880) {
      showToast({ message: 'Logo file size must be less than 5MB. We will compress it automatically.', variant: 'warning' });
      return;
    }
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      showToast({ message: 'Please select an image file', variant: 'warning' });
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      
      // Compress image if it's too large
      compressImage(dataUrl, (compressedDataUrl) => {
        setBusinessSettings(prev => ({
          ...prev,
          logoDataUrl: compressedDataUrl,
          logoFile: file
        }));
      });
    };
    reader.readAsDataURL(file);
  };
  
  // Enhanced image compression with multiple quality levels
  const compressImage = (dataUrl, callback) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Calculate new dimensions (max 200x200)
      const maxSize = 200;
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw image
      ctx.drawImage(img, 0, 0, width, height);
      
      // Try multiple quality levels to find the best compression
      const qualityLevels = [0.9, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3];
      const maxSizeBytes = 900000; // 900KB to leave buffer for Firestore
      
      for (let i = 0; i < qualityLevels.length; i++) {
        const quality = qualityLevels[i];
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        
        if (compressedDataUrl.length <= maxSizeBytes) {
          callback(compressedDataUrl);
          return;
        }
      }
      
      // If all quality levels are too large, use the smallest size
      const finalCompressed = canvas.toDataURL('image/jpeg', 0.3);
      callback(finalCompressed);
    };
    img.src = dataUrl;
  };
  
  // Remove logo
  const removeLogo = () => {
    setBusinessSettings(prev => ({
      ...prev,
      logoDataUrl: '',
      logoFile: null
    }));
  };
  
  // Generate preview
  const generatePreview = async () => {
    if (previewLoading) return; // Prevent multiple simultaneous requests
    
    setPreviewLoading(true);
    try {
      // Validate business settings
      if (!businessSettings.name.trim()) {
        showToast({ message: 'Please enter a business name first.', variant: 'warning' });
        return;
      }
      
      const imageBlob = await invoiceImageService.generateInvoiceImage(
        sampleInvoiceData, 
        businessSettings
      );
      
      // Clean up previous image
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
      
      const imageUrl = URL.createObjectURL(imageBlob);
      setPreviewImage(imageUrl);
      setShowPreview(true);
    } catch (error) {
      console.error('Failed to generate preview:', error);
      showToast({ message: 'Failed to generate preview. Please try again.', variant: 'error' });
    } finally {
      setPreviewLoading(false);
    }
  };
  
  // Close preview
  const closePreview = () => {
    if (previewImage) {
      URL.revokeObjectURL(previewImage);
    }
    setShowPreview(false);
    setPreviewImage(null);
  };

  // If used as modal and not open, don't render
  if (open !== undefined && !open) {
    return null;
  }

  if (loading) {
  return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  const content = (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-green-100 px-4 py-3">
        <h1 className="text-lg font-semibold text-gray-800">📄 Invoice Editor</h1>
        <p className="text-sm text-gray-600">Customize your invoice design and business information</p>
        </div>

      <div className="p-4 space-y-6">
        {/* Business Information Section */}
        <div className="bg-white rounded-lg border border-green-100 p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Business Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Business Name */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Business Name *</label>
              <input
                type="text"
                value={businessSettings.name}
                onChange={(e) => setBusinessSettings(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Enter your business name"
              />
                </div>
            
            {/* Address */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
              <textarea
                value={businessSettings.address}
                onChange={(e) => setBusinessSettings(prev => ({ ...prev, address: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                rows="3"
                placeholder="Enter your business address"
              />
                </div>
            
            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
              <input
                type="tel"
                value={businessSettings.phone}
                onChange={(e) => setBusinessSettings(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Enter phone number"
              />
        </div>

            {/* Email */}
                <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={businessSettings.email}
                onChange={(e) => setBusinessSettings(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Enter email address"
              />
                </div>
                </div>
              </div>

        {/* Footer Text Section */}
        <div className="bg-white rounded-lg border border-green-100 p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Invoice Footer Message</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Thank You Message
            </label>
            <textarea
              value={businessSettings.footerText}
              onChange={(e) => setBusinessSettings(prev => ({ ...prev, footerText: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              rows="3"
              placeholder="Thank you for your business!"
            />
            <p className="text-xs text-gray-500 mt-1">
              This message will appear at the bottom of all invoices. Leave empty to use default: "Thank you for your business!"
            </p>
          </div>
        </div>

        {/* Logo Section */}
        <div className="bg-white rounded-lg border border-green-100 p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Business Logo</h2>
          
          <div className="flex flex-col md:flex-row gap-6">
            {/* Logo Preview */}
            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-gray-700 mb-2">Current Logo</label>
              <div className="w-32 h-32 border-2 border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                {businessSettings.logoDataUrl ? (
                  <img
                    src={businessSettings.logoDataUrl}
                    alt="Business Logo"
                    className="w-full h-full object-contain rounded-lg"
                  />
                ) : (
                  <div className="text-gray-400 text-center">
                    <div className="text-2xl mb-1">📷</div>
                    <div className="text-xs">No Logo</div>
                    </div>
              )}
                    </div>
                  </div>
            
            {/* Logo Upload */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Upload Logo</label>
              <div className="space-y-3">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <div className="text-xs text-gray-500">
                  <p>• Maximum file size: 5MB (will be compressed automatically)</p>
                  <p>• Supported formats: JPG, PNG, GIF</p>
                  <p>• Will be resized to 200x200 pixels and optimized</p>
                </div>
                {businessSettings.logoDataUrl && (
                  <button
                    onClick={removeLogo}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Remove Logo
                  </button>
              )}
            </div>
          </div>
            </div>
          </div>

        {/* Preview Section */}
        <div className="bg-white rounded-lg border border-green-100 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Invoice Preview</h2>
            <button
              onClick={generatePreview}
              disabled={previewLoading || !businessSettings.name.trim()}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {previewLoading ? 'Generating...' : 'Generate Preview'}
            </button>
              </div>

          <div className="text-sm text-gray-600 mb-4">
            Preview will show sample invoice with your business information and logo.
                </div>
          
          {showPreview && previewImage && (
            <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-medium text-gray-800">Invoice Preview</h3>
                <button
                  onClick={closePreview}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
                </div>
              <div className="flex justify-center">
                <img
                  src={previewImage}
                  alt="Invoice Preview"
                  className="max-w-full h-auto border border-gray-200 rounded-lg shadow-sm"
                />
              </div>
            </div>
          )}
          </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={saveBusinessSettings}
            disabled={!businessSettings.name.trim()}
            className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            Save Settings
          </button>
            </div>
            </div>
          </div>
  );

  // If used as modal, wrap in modal overlay
  if (open !== undefined) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          {/* Background overlay */}
          <div 
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={onClose}
          ></div>
          
          {/* Modal content */}
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
            {/* Close button */}
            <div className="absolute top-4 right-4 z-10">
              <button
                onClick={onClose}
                className="bg-white rounded-full p-2 shadow-lg hover:bg-gray-50 transition-colors"
              >
                <span className="text-gray-500 text-xl">×</span>
              </button>
        </div>

            {content}
        </div>
      </div>
    </div>
  );
}

  // If used as standalone page, return content directly
  return content;
};

export default InvoiceEditor;